import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Seo } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { api } from "@/lib/api";
import { CONTACT, fmtDate, FALLBACK_IMAGE } from "@/lib/site";

export default function PastStudents() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api
      .get("/blog", { params: { category: "Past Students" } })
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]));
  }, []);

  const [featured, ...rest] = posts || [];

  return (
    <>
      <Seo
        title="Past Students"
        description={"Where Kerry's dancers went next — vocational colleges, professional stages and lifelong confidence. Success stories from 17 years of dance teaching in Whitstable."}
        path="/past-students"
      />
      <section className="grain border-b-[0.5px] border-foreground/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground mb-6">
              17 years of dancers &middot; Whitstable and beyond
            </p>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              Where they
              <br />
              <span className="text-[hsl(var(--kerrys-sea))]">went next</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Vocational colleges, professional stages, and a whole lot of confident grown-ups who still remember their
              first show. These are our past students&rsquo; stories.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {posts === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-none" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="border-[0.5px] border-dashed border-foreground/60 py-20 px-6 text-center" data-testid="past-students-empty-state">
            <p className="font-display uppercase text-4xl">Stories coming soon</p>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
              We&rsquo;re gathering stories from dancers who trained with us. Danced here yourself? We&rsquo;d love to hear
              from you.
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {featured && (
              <FadeUp>
                <Link
                  to={`/blog/${featured.slug}`}
                  className="group grid grid-cols-1 lg:grid-cols-12 border-[0.5px] border-foreground bg-card"
                  data-testid="past-students-featured-card"
                >
                  <div className="lg:col-span-7 overflow-hidden lg:border-r-[0.5px] border-foreground">
                    <img
                      src={featured.image_url || FALLBACK_IMAGE}
                      alt={featured.title}
                      className="w-full h-72 lg:h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-center border-t-[0.5px] lg:border-t-0 border-foreground">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))] flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" /> {fmtDate(featured.created_at)}
                    </p>
                    <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92] mt-5 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-muted-foreground mt-5 leading-relaxed line-clamp-3">{featured.excerpt}</p>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-foreground mt-7">Read the story &rarr;</p>
                  </div>
                </Link>
              </FadeUp>
            )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {rest.map((p, i) => (
                <FadeUp key={p.id} delay={i * 0.05} className={i % 2 === 0 ? "md:col-span-7" : "md:col-span-5 md:mt-14"}>
                  <Link to={`/blog/${p.slug}`} className="group block border-[0.5px] border-foreground bg-card h-full" data-testid="past-students-post-card">
                    <div className="overflow-hidden border-b-[0.5px] border-foreground">
                      <img
                        src={p.image_url || FALLBACK_IMAGE}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-52 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMAGE;
                        }}
                      />
                    </div>
                    <div className="p-7">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">{fmtDate(p.created_at)}</p>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl leading-[0.95] mt-3 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                        {p.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        )}

        <FadeUp className="mt-[120px]">
          <div className="bg-[hsl(var(--kerrys-sun))] border-[0.5px] border-foreground grain px-6 sm:px-12 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div>
              <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92]">Danced with us back in the day?</h2>
              <p className="text-foreground/70 mt-3 max-w-lg">
                Tell Kerry where dance took you &mdash; we&rsquo;d love to feature your story on this page.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12 shrink-0"
              data-testid="past-students-share-button"
            >
              <a href={CONTACT.emailHref}>
                <Mail className="h-4 w-4 mr-2" /> Share your story
              </a>
            </Button>
          </div>
        </FadeUp>

        <FadeUp className="mt-10">
          <div className="flex items-center justify-between gap-4 border-t-[0.5px] border-foreground/40 pt-8">
            <p className="text-sm text-muted-foreground">Your kid could be next on this wall.</p>
            <Button
              asChild
              variant="outline"
              className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
              data-testid="past-students-book-button"
            >
              <Link to="/book">
                Book a first class <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </FadeUp>
      </section>
    </>
  );
}
